import React from 'react';
import { CompositeCard as CompositeCardType, CardSectionProps } from '../../types/compositeCards';
import { cardRegistry } from './Registry/CardComponentRegistry';

interface CompositeCardProps {
  card: CompositeCardType;
  onSelect?: (cardId: string) => void;
  onCollapse?: (cardId: string, collapsed: boolean) => void;
  onPin?: (cardId: string, pinned: boolean) => void;
  onTrigger?: (trigger: any) => void;
  onAction?: (action: string, payload?: any) => void;
}

export const CompositeCard: React.FC<CompositeCardProps> = ({
  card,
  onSelect,
  onCollapse,
  onPin,
  onTrigger,
  onAction
}) => {
  const { layout, isCollapsed = false } = card;
  const { styling, sections } = layout;

  // Filter sections based on role and model visibility
  const visibleSections = sections.filter(section => {
    const roleVisible = section.roleVisibility.includes(card.context.uiRole);
    const modelVisible = section.modelVisibility.includes(card.context.modelType);
    return roleVisible && modelVisible;
  });

  const handleSectionAction = (action: string, payload?: any) => {
    onAction?.(action, { ...payload, cardId: card.id });
  };

  const handleCollapse = () => {
    onCollapse?.(card.id, !isCollapsed);
  };

  const handlePin = () => {
    onPin?.(card.id, !card.isPinned);
  };

  const handleSelect = () => {
    onSelect?.(card.id);
    
    // Trigger context display for artifact cards
    if (card.type === 'artifact' && card.data && 'artifact' in card.data) {
      onAction?.('show-context', {
        type: 'artifact',
        artifact: card.data.artifact
      });
    }
  };

  const renderSection = (section: any) => {
    // Only log in development mode and sample to reduce verbosity
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
      console.log('🔍 [CompositeCard] Rendering section (sampled):', {
        sectionId: section.id,
        sectionType: section.type,
        component: section.component,
        cardType: card.type
      });
    }
    
    const Component = cardRegistry.getComponent(
      card.type,
      section.component,
      card.context.uiRole,
      card.context.modelType
    );

    if (!Component) {
      // Always log warnings for missing components
      if (process.env.NODE_ENV === 'development') {
        console.warn(`⚠️ [CompositeCard] Component not found for section:`, {
          sectionComponent: section.component,
          cardType: card.type,
          uiRole: card.context.uiRole,
          modelType: card.context.modelType
        });
      }
      return null;
    }
    
    // Only log in development mode and sample to reduce verbosity
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
      console.log('✅ [CompositeCard] Component found, rendering (sampled):', section.component);
    }

    const sectionProps: CardSectionProps = {
      section,
      card,
      onAction: handleSectionAction,
    };

    // Add specific props for certain section types
    if (section.type === 'header') {
      const headerProps = {
        ...sectionProps,
        onCollapse: handleCollapse,
        onPinToggle: handlePin,
        onSelect: handleSelect
      };
      return <Component {...headerProps} />;
    }

    return <Component {...sectionProps} />;
  };

  return (
    <div
      style={{
        backgroundColor: styling.backgroundColor,
        border: `${styling.borderWidth} solid ${styling.borderColor}`,
        borderRadius: styling.borderRadius,
        boxShadow: styling.boxShadow,
        padding: '16px',
        color: styling.color,
        maxWidth: layout.maxWidth || '600px',
        minHeight: layout.minHeight || '150px',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 8px 12px ${styling.borderColor}30`;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = styling.boxShadow;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      onClick={() => handleSelect()}
    >
      {visibleSections.map((section) => (
        <div key={section.id}>
          {renderSection(section)}
          {/* Don't render content sections when collapsed */}
          {isCollapsed && section.type === 'content' && (
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '8px'
            }}>
              Click to expand
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
